import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { pure } from 'recompose';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import noop from 'lodash/noop';

import { primaryMedium, primaryMediumAccessible } from 'styleVars/getColors';
import { Icon, Loader } from 'shared/components';
import { AccessibilityContext } from 'App';
import mediaStatus from 'modules/appointmentInfo/constants/mediaStatus';
import mediaType from '../mediaType';

import theme from '../MediaGallery.scss';

const IconByStatus = ({ status }) => {
  let StatusIcon = null;
  const { isAccessibilityEnabled } = useContext(AccessibilityContext);
  switch (status) {
    case mediaStatus.FAILED:
      StatusIcon = (<Icon name="exclamation-outline" />);
      break;
    case mediaStatus.CREATED:
      StatusIcon = (<Icon name="clock-outline" />);
      break;
    case mediaStatus.REJECTED:
      StatusIcon = (<Icon name="not-approved" />);
      break;
    case mediaStatus.LOADING:
      StatusIcon = (<Loader color={ isAccessibilityEnabled ? primaryMediumAccessible : primaryMedium } />);
      break;
    default:
      break;
  }

  return (
    <div className={ classnames(theme.iconHolder, {
      [theme.uploadFailed]: status === mediaStatus.FAILED,
    }) }
    >
      { StatusIcon }
    </div>
  );
};

const IconByType = ({ mimeType, status }) => {
  const { PDF, JPG, JPEG, PNG } = mediaType;

  if (mimeType === PDF) {
    return <Icon className={ theme.iconType } name="pdf-file" />;
  }

  if (status !== mediaStatus.UPLOADED) {
    switch (mimeType) {
      case JPG:
      case JPEG:
        return <Icon className={ theme.iconType } name="jpg-file" />;
      case PNG:
        return <Icon className={ theme.iconType } name="png-file" />;
      default:
        return null;
    }
  }

  return null;
};

const DeleteButton = ({ onDelete, id, comment }) => (
  <button
    className={ theme.deleteButtonIcon }
    onClick={ (e) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete({ id, comment });
    } }
  >
    <Icon name="trash">
      <div className={ theme.deleteToolTip }>
        <FormattedMessage
          id="const.mediaViewer.mediaViewControl.delete"
          defaultMessage="** Delete"
        />
      </div>
    </Icon>
  </button>
);

const EditButton = ({ onEdit, id, thumbnail, mimeType, comment }) => (
  <button
    className={ theme.updateButtonIcon }
    onClick={ (e) => {
      e.preventDefault();
      e.stopPropagation();
      onEdit({ id, thumbnail, mimeType, comment });
    } }
  >
    <Icon name="pencil">
      <div className={ theme.editToolTip }>
        <FormattedMessage
          id="Media.Update.ThmbnailButton.Tooltip"
          defaultMessage="** Edit file name"
        />
      </div>
    </Icon>
  </button>
);

const Thumbnail = ({ thumbnail, mimeType, comment, status }) => {
  if (thumbnail
    && (
      (mimeType !== mediaType.PDF)
      || (mimeType === mediaType.PDF && status === mediaStatus.UPLOADED)
    )
  ) {
    return (
      <img
        src={ thumbnail }
        alt={ comment }
      />
    );
  }

  return null;
};

const ImageCaption = ({ comment }) => (
  <div
    className={ theme.caption }
    title={ comment }
  >
    { comment }
  </div>
);

const propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  thumbnail: PropTypes.string,
  status: PropTypes.string,
  downloadRoute: PropTypes.string,
  comment: PropTypes.string,
  name: PropTypes.string,
  mimeType: PropTypes.string,
  canBeRemoved: PropTypes.bool,
  canBeUpdated: PropTypes.bool,
};

const defaultProps = {
  onClick: noop,
  onDelete: noop,
  onEdit: noop,
  status: mediaStatus.UPLOADED,
  downloadRoute: '',
  thumbnail: '',
  comment: '',
  name: '',
  mimeType: '',
  canBeRemoved: false,
  canBeUpdated: false,
};

const MediaGalleryTile = ({
  id,
  onClick,
  onDelete,
  onEdit,
  status,
  downloadRoute,
  name,
  comment,
  thumbnail,
  mimeType,
  canBeRemoved,
  canBeUpdated,
}) => {
  const deleteButtonProps = {
    onDelete,
    id,
    comment,
  };

  const editButtonProps = {
    onEdit,
    id,
    thumbnail,
    mimeType,
    comment,
  };

  const thumbnailProps = {
    thumbnail,
    mimeType,
    comment,
    status,
  };

  const {
    FAILED,
    REJECTED,
    CREATED,
  } = mediaStatus;

  return (
    <button
      className={ classnames(theme.thumb, {
        [theme.blackoutActive]: [FAILED, REJECTED, CREATED].includes(status),
      }) }
      aria-label={ comment }
      onClick={ () => onClick({ id, status, mimeType, downloadRoute, name }) }
    >
      { canBeUpdated && <EditButton { ...editButtonProps } /> }
      { canBeRemoved && <DeleteButton { ...deleteButtonProps } /> }

      <div className={ theme.imgHolder }>

        <Thumbnail { ...thumbnailProps } />

        <IconByType mimeType={ mimeType } status={ status } />

        <div className={ theme.blackoutHolder } />

        <div className={ classnames(theme.iconHolder, {
          [theme.uploadFailed]: status === FAILED,
        }) }
        >
          <IconByStatus status={ status } />
        </div>

      </div>

      <ImageCaption comment={ comment } />
    </button>
  );
};


MediaGalleryTile.propTypes = propTypes;
MediaGalleryTile.defaultProps = defaultProps;

export default pure(MediaGalleryTile);
